




































































































































































































import {
  Component,
  Vue,
  Prop,
  Watch,
  PropSync,
  Ref,
} from "vue-property-decorator";
import { Getter, Action } from "vuex-class";
import RightDrawer from "@/components/misc/RightDrawer.vue";
import {
  BaseMessageTypeEnum,
  ComposedMessage,
  MessageSourceTypeEnum,
  MessageViewModel,
  NewMessageModel,
  Recipient,
} from "./types";
import RichTextEditor from "@/components/misc/RichTextEditor.vue";
import { MessageSourceType } from "@/shared/models/messageSourceType";
import { Notification } from "@/store/modules/notifications";
import { ListModel } from "@/shared/models/listModel";
import { VFinancialDocument } from "@/shared/models/VFinancialDocument";
import * as _ from "lodash";
import { FinancialDocumentStatusEnum } from "@/shared/models/enums.type";
import { NotificationTemplateViewModel } from "./templates/types";
import { LanguageView } from "@/shared/models/languages";
import { DateTime } from "luxon";
import { saveFile } from "@/shared/saveFile";
import brokenRuleAdapter from "@/shared/handleBrokenRuleAdapter";

@Component({
  components: {
    RightDrawer,
    RichTextEditor,
  },
})
export default class MessageForm extends Vue {
  @PropSync("open") internalOpen?: boolean;
  @Prop({ default: () => [] }) recipients?: Recipient[];
  @Prop() baseMessage?: MessageViewModel;
  @Prop() baseMessageType?: number;

  @Getter("appParams/cashRegisterFeatureFlag")
  cashRegisterFeatureFlag?: boolean;

  fkey = 0;
  translateWarning = false;

  get isReplyOrForward() {
    return this.baseMessageType != BaseMessageTypeEnum.NewMessage
      ? true
      : false;
  }

  get attachments() {
    return this.baseMessage?.messageAttachments ?? [];
  }

  formatRecipients(item: MessageViewModel) {
    let newMessageRecipients: Recipient[] = [];

    if (
      this.baseMessageType == BaseMessageTypeEnum.Reply ||
      this.baseMessageType == BaseMessageTypeEnum.ReplyToAll
    ) {
      newMessageRecipients = [
        {
          name: "",
          type: "Original sender",
          email: item.from,
        } as Recipient,
      ];
    }

    if (this.baseMessageType == BaseMessageTypeEnum.ReplyToAll) {
      newMessageRecipients.push(
        ...item.messageRecipients
          .filter((y) => y.to !== item.from && !y.isBcc)
          .map((x) => {
            return {
              name: "",
              type: "Recipient",
              email: x.to,
            } as Recipient;
          })
      );
    }

    return newMessageRecipients;
  }

  formatSubject(item: MessageViewModel) {
    if (
      this.baseMessageType == BaseMessageTypeEnum.Reply ||
      this.baseMessageType == BaseMessageTypeEnum.ReplyToAll
    ) {
      if (item.subject.startsWith("RE: ") || item.subject.startsWith("RE:")) {
        return item.subject;
      } else {
        return `RE: ${item.subject}`;
      }
    } else if (this.baseMessageType == BaseMessageTypeEnum.Forward) {
      if (item.subject.startsWith("FW: ") || item.subject.startsWith("FW:")) {
        return item.subject;
      } else {
        return `FW: ${item.subject}`;
      }
    }

    return item.subject;
  }

  @Watch("open")
  openWatch(fresh: boolean) {
    if (fresh) {
      this.fkey++;
      this.templateId = null;
      this.templateLanguage = this.internalLanguage;

      // set default values
      this.message = new NewMessageModel();

      this.isMsgSent = false;
      this.uploadedFiles = [];

      // set default source type
      this.message.reservationId = this.baseMessage?.reservationId ?? null;
      this.message.customerId = this.baseMessage?.customerId ?? null;
      this.message.sourceTypeId = MessageSourceTypeEnum.EMail;
      this.loadTemplates();

      if (this.baseMessage && this.isReplyOrForward) {
        this.message.attachmentsIds = this.baseMessage.messageAttachments.map(
          (x) => x.documentId
        );
        this.uploadedFiles = this.baseMessage.messageAttachments.map((x) => {
          return { filename: x.name, id: x.documentId, forwarded: true };
        });
        this.message.parentMessageId = this.baseMessage.id ?? null;
        this.message.subject = this.formatSubject(this.baseMessage);
        this.recipientsArray = this.formatRecipients(this.baseMessage);
        this.recipientsChanged(this.recipientsArray);
      }
      // set default recipient if they have the correct message source type value (email, phone number, etc.)
      else if (this.message.customerId) {
        const recipient = this.recipients?.find(
          (x) => x.id == this.message.customerId
        );
        if (recipient) {
          switch (this.message.sourceTypeId) {
            case MessageSourceTypeEnum.EMail: {
              if (recipient.email) this.recipientsArray = [recipient];
              break;
            }
            case MessageSourceTypeEnum.SMS: {
              if (recipient.telephoneNumber) this.recipientsArray = [recipient];
              break;
            }
            case MessageSourceTypeEnum.WhatsApp: {
              if (recipient.telephoneNumber) this.recipientsArray = [recipient];
              break;
            }
            default: {
              break;
            }
          }
          this.recipientsChanged(this.recipientsArray);
        }
      }

      // get available documents if message is for reservation
      if (this.message.reservationId) {
        this.getReservationDocuments();
      }

      // set initial message for change tracking
      this.initialMessage = _.cloneDeep(this.message);
    } else {
      this.deleteAttachments();
    }
  }

  @Ref("drawer") rightDrawer?: RightDrawer;
  close() {
    this.rightDrawer?.clickOverlay();
  }

  @Getter("codebooks/messageSourceTypes")
  sourceTypes?: MessageSourceType[];
  @Action("codebooks/refreshMessageSourceTypes")
  refreshSourceTypes?: () => Promise<void>;

  @Getter("language/language") language?: string;
  get internalLanguage() {
    return this.language?.split('-')[0] ?? "hr-HR";
  }
  @Getter("codebooks/languages")
  languages?: LanguageView[];
  @Action("codebooks/refreshLanguages")
  refreshLanguages?: () => Promise<void>;

  async created() {
    const promises = [];

    if ((this.sourceTypes?.length ?? 0) === 0 && this.refreshSourceTypes) {
      promises.push(this.refreshSourceTypes());
    }

    if ((this.languages?.length ?? 0) === 0 && this.refreshLanguages) {
      promises.push(this.refreshLanguages());
    }

    await Promise.all(promises);
  }

  sourceTypeDisabled(type: MessageSourceType) {
    return type.id != MessageSourceTypeEnum.EMail && type.id != MessageSourceTypeEnum.SMS;
  }

  templateLanguage = "";
  templateId = null as number | null;
  loadingTemplate = false;
  templates = [] as NotificationTemplateViewModel[];

  get filteredTemplates() {
    return this.templates.filter((x) => x.sourceTypeIds?.includes(this.message.sourceTypeId))
  }
  
  loadTemplates() {
    this.axios
      .get<ListModel<NotificationTemplateViewModel>>("/notificationTemplates", {})
      .then((res) => {
        this.templates = res.data.result;
      });
  }
  
  onTemplateChange(missingTranslate: string[]) {
    const handleCodes = ["MessageValidation.TemplateTranslationMissing"];
    const adapter = brokenRuleAdapter(this.axios, handleCodes);

    this.additionalRecipients = [];

    if (!this.templateId || !this.message.reservationId) return;

    this.loadingTemplate = true;
    this.axios
      .get<ComposedMessage>("notificationTemplates/GetComposedMessage", {
        params: {
          templateId: this.templateId,
          reservationId: this.message.reservationId,
          cultureCode: this.templateLanguage ?? this.internalLanguage,
          overrides: missingTranslate
        },
         ...(missingTranslate.length !== 0 ? { adapter } : {})
      })
      .then((res) => {
        this.additionalRecipients.push(...res.data.additionalRecipients.map(x => {
          return {
            id: 0,
            type: x.type,
            name: x.name,
            email: x.email
          } as Recipient
        }));

        this.message.subject =
          this.baseMessageType == BaseMessageTypeEnum.NewMessage
            ? res.data.subject
            : this.message.subject;
        this.message.body = res.data.body;
        this.message.attachedDocumentIds = this.documents
          .filter((x) =>
            res.data.attachmentDocumentsIds.includes(x.financialDocumentId)
          )
          .map((x) => x.financialDocumentId);

        if (this.baseMessage && this.isReplyOrForward) {
          this.recipientsArray = this.formatRecipients(this.baseMessage);
          this.recipientsChanged(this.recipientsArray);
        } else {
          let recipients = [
            ...(this.recipients?.filter((x) =>
              res.data.recipientsIds.includes(x.id)
            ) ?? []),
          ];

          // filter out recipients with missing source type value (email, phone number, etc.)
          switch (this.message.sourceTypeId) {
            case MessageSourceTypeEnum.EMail: {
              recipients = recipients.filter((x) => x.email).concat(this.additionalRecipients.filter(x => x.email));
              break;
            }
            case MessageSourceTypeEnum.SMS: {
              recipients = recipients.filter((x) => x.telephoneNumber);
              break;
            }
            case MessageSourceTypeEnum.WhatsApp: {
              recipients = recipients.filter((x) => x.telephoneNumber);
              break;
            }
            default: {
              recipients = [];
              break;
            }
          }

          this.recipientsArray = recipients;
          this.recipientsChanged(this.recipientsArray);
          this.translateWarning = false;
        }
      })
       .catch(() => {
          this.translateWarning = true;
      })
      .finally(() => {
        this.loadingTemplate = false;
      });
  }

  get baseMessageBody() {
    let bodyToReturn = "";

    if (this.baseMessage) {
      const date = DateTime.fromISO(this.baseMessage.date);
      bodyToReturn = bodyToReturn
        .concat(`<br><hr>`)
        .concat(`<div>From: ${this.baseMessage.from}</div>`)
        .concat(`<div>Sent: ${date.toFormat("dd.MM.yyyy HH:mm")}</div>`)
        .concat(
          `<div>To: ${this.baseMessage.messageRecipients
            .filter((y) => !y.isCc && !y.isBcc)
            .map((x) => x.to)
            .join("; ")}</div>`
        )
        .concat(
          `<div>Cc: ${this.baseMessage.messageRecipients
            .filter((y) => y.isCc)
            .map((x) => x.to)
            .join("; ")}</div>`
        )
        .concat(`<div>Subject: ${this.baseMessage.subject}</div><br>`)
        .concat(this.baseMessage.body);
    }

    return bodyToReturn;
  }

  recipientsArray: Recipient[] = [];

  message = new NewMessageModel();
  initialMessage = new NewMessageModel();
  isFormValid = false;
  isSending = false;
  get sendingDisabled() {
    return !this.isFormValid || this.isSending;
  }

  additionalRecipients: Recipient[] = [];
  get internalRecipients() {
    const recipients = this.recipients?.concat(...this.additionalRecipients);

    switch (this.message.sourceTypeId) {
      case MessageSourceTypeEnum.EMail:
        if (this.isReplyOrForward && this.baseMessage) {
          return this.formatRecipients(this.baseMessage);
        } else {
          return recipients?.filter((x) => x.email) ?? [];
        }
      case MessageSourceTypeEnum.SMS:
        return this.recipients?.filter((x) => x.telephoneNumber) ?? [];
      case MessageSourceTypeEnum.WhatsApp:
        return this.recipients?.filter((x) => x.telephoneNumber) ?? [];
      default:
        return [];
    }
  }

  @Watch("message.sourceTypeId")
  watchSourceTypeId() {
    this.recipientsArray = [];
    this.message.body = "";
    switch (this.message.sourceTypeId) {
      case MessageSourceTypeEnum.EMail: {
        const recipientEmail = this.recipientsArray.find(x => x.type === 'Reservation holder');
        this.message.to = [recipientEmail?.email ?? ''];
        break;
      }
      case MessageSourceTypeEnum.SMS: {
        const recipientSMS = this.recipientsArray.find(x => x.type === 'Reservation holder');
        this.message.to = [recipientSMS?.telephoneNumber ?? ''];
        break;
      } 
      case MessageSourceTypeEnum.WhatsApp: {
        const recipientSMS = this.recipientsArray.find(x => x.type === 'Reservation holder');
        this.message.to = [recipientSMS?.telephoneNumber ?? ''];
        break;
      } 
    }
  }

  get isSMSOrWhatsApp() {
    return this.message.sourceTypeId == MessageSourceTypeEnum.WhatsApp || this.message.sourceTypeId == MessageSourceTypeEnum.SMS;
  }

  getItemText(item: Recipient) {
    switch (this.message.sourceTypeId) {
      case MessageSourceTypeEnum.EMail:
        return `${item.name} (${this.$t(item.type)}) - ${item.email}`;
      case MessageSourceTypeEnum.SMS:
        return `${item.name} (${this.$t(item.type)}) - ${item.telephoneNumber}`;
      case MessageSourceTypeEnum.WhatsApp:
        return `${item.name} (${this.$t(item.type)}) - ${item.telephoneNumber}`;
      default:
        return item.name;
    }
  }

  getItemValue(item: Recipient) {
    switch (this.message.sourceTypeId) {
      case MessageSourceTypeEnum.EMail:
        return item.email;
      case MessageSourceTypeEnum.SMS:
        return item.telephoneNumber;
      case MessageSourceTypeEnum.WhatsApp:
        return item.telephoneNumber;
      default:
        return item.name;
    }
  }

  recipientsChanged(values: any[]) {
    const result = [] as string[];
    switch (this.message.sourceTypeId) {
      case MessageSourceTypeEnum.EMail: {
        const maped = values.map((x) =>
          typeof x == "string" ? (x as string) : (x.email as string)
        );
        result.push(...maped);
        break;
      }
      case MessageSourceTypeEnum.SMS: {
        const maped = values.map((x) =>
          typeof x == "string" ? (x as string) : (x.telephoneNumber as string)
        );
        result.push(...maped);
        break;
      }
      case MessageSourceTypeEnum.WhatsApp: {
        const maped = values.map((x) =>
          typeof x == "string" ? (x as string) : (x.telephoneNumber as string)
        );
        result.push(...maped);
        break;
      }
      default: {
        break;
      }
    }

    this.message.to = result;
  }

  loading = false;
  documents: VFinancialDocument[] = [];
  excludeDocumentStatues = [FinancialDocumentStatusEnum.UIzradi];
  getReservationDocuments() {
    if (!this.message.reservationId) return;

    this.axios
      .get<ListModel<VFinancialDocument>>("/financialDocuments", {
        params: {
          $top: 1000,
          reservationId: this.message.reservationId,
          statusIdsExcluded: this.excludeDocumentStatues.join(","),
        },
      })
      .then((x) => {
        this.documents = x.data.result;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  uploadedFiles: any[] = [];
  isMsgSent = false;

  sendMessage() {
    this.isSending = true;

    if(this.message.sourceTypeId === MessageSourceTypeEnum.SMS || this.message.sourceTypeId === MessageSourceTypeEnum.WhatsApp)
    {
      this.message.body = this.message.body.replace(/<(.|\n)*?>/g, '');
    }

    this.axios
      .post("/messages/SendMessage", this.message)
      .then(() => {
        this.$store.dispatch("showNotification", {
          color: "success",
          message: this.$t("Message.SendSuccess"),
          timeout: 5000,
        } as Notification);
        this.$emit("messageSent");
        this.initialMessage = _.cloneDeep(this.message);
        this.internalOpen = false;
        this.isMsgSent = true;
      })
      .finally(() => {
        this.isSending = false;
      });
  }

  deleteAttachments(file?: any) {
    if (this.isMsgSent || this.message.attachmentsIds.length < 1) {
      return;
    }

    if (
      this.baseMessageType == BaseMessageTypeEnum.Forward &&
      file &&
      file.forwarded
    ) {
      this.message.attachmentsIds = this.message.attachmentsIds.filter(
        (x) => x !== file.id
      );
      this.uploadedFiles = this.uploadedFiles.filter((x) => x.id !== file.id);

      return;
    }

    let ids = [];
    if (file) {
      ids.push(file.id);
    } else {
      ids = this.uploadedFiles
        .filter((x) => x.forwarded != true)
        .map((x) => x.id);
    }
    if (ids.length > 0) {
      this.isSending = true;
      this.axios
        .delete("/documents/deleteDocuments", { data: { ids: ids } })
        .then(() => {
          if (file) {
            this.message.attachmentsIds = this.message.attachmentsIds.filter(
              (x) => x !== file.id
            );
            this.uploadedFiles = this.uploadedFiles.filter(
              (x) => x.id !== file.id
            );
          } else {
            this.message.attachmentsIds = [];
            this.uploadedFiles = [];
          }
        })
        .finally(() => {
          this.isSending = false;
        });
    }
  }

  @Ref("fileInput") fileInput?: HTMLInputElement;

  onPickFile() {
    this.fileInput?.click();
  }

  onFilesPicked() {
    const files = this.fileInput?.files;

    if (!files) {
      return;
    }

    this.isSending = true;
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    this.axios
      .post("/documents/uploadDocuments", formData)
      .then((res) => {
        this.message.attachmentsIds = this.message.attachmentsIds.concat(
          res.data.map((x: any) => x.id)
        );
        this.uploadedFiles = this.uploadedFiles.concat(res.data);
      })
      .catch(() => {
        //google Chrome did not fire the change event on the same file, this reset input
        if (this.fileInput) {
          this.fileInput.value = "";
        }
      })
      .finally(() => {
        this.isSending = false;
      });
  }

  async downloadAttachment(documentId: number, attachmentName: string) {
    await this.$http
      .get(`documents/${documentId}`, {
        responseType: "blob",
      })
      .then((res) => {
        const groups = /filename=(?<attachmentName>[^;]+);/.exec(
          res.headers["content-disposition"]
        )?.groups;
        saveFile(res.data, groups?.filename ?? attachmentName);
      });
  }

  get rules() {
    return {
      required: (value: any) => !!value || this.$t("Validation.Required"),
      recipientsRequired: (value: string[]) =>
        value?.length > 0 || this.$t("Validation.Required"),
      emails: (value: string[]) => {
        if(this.message.sourceTypeId == MessageSourceTypeEnum.EMail) {
          return !value?.some((x) => !/^$|.+@.+\..+/.test(x)) || this.$t("Validation.EmailFormat");
        } else {
          return true;
        }
      },
      phone: (value: string[]) => {
        if(this.message.sourceTypeId == MessageSourceTypeEnum.SMS || this.message.sourceTypeId == MessageSourceTypeEnum.WhatsApp) {
          return !value?.some((x) => !/^\+[1-9]\d{1,14}$/.test(x)) || this.$t("Validation.PhoneFormat");
        } else {
          return true;
        }
      }
    };
  }
}
